import type { ReactNode } from "react";
import { Button, CustomerAddresses, Notice, SiteMark } from "@omnabase/ui";
import { siteDeliveryLine, siteLabel, type Customer, type Ticket } from "@omnabase/shared";
import { addressRows } from "../../lib/customers";
import { siteReplies } from "../../lib/webhooks";

/** M12 round two: what a ticket shows when a connected site filed it through the public API (the prototype's `TicketSite.tsx`, round zero).
 *  The band names the site ("Via forum.kriesi.at") and links to the topic on the site; a public topic carries its mark ("Public on
 *  forum.kriesi.at"). Who gets a reply and how it goes is the To line's (the To line round, 2026-10-30: the route line under the box is gone).
 *  Round three: a reply on its way to the site has its own line under it ("Waiting for …", "Delivered to …", "Not delivered to … · sent by
 *  mail to …"), and a reply the site never took (the address not confirmed, so nothing was mailed) puts the banner on the ticket with "Send by
 *  mail" and "Try again". Tickets that came another way show none of it. */
export type SiteTicketView = { channelLabel?: string; mark?: ReactNode; banner: ReactNode;
  /** the site-fields round, rule 8: the title's link to the ticket's place on its site, with the tooltip "Open on <host>"; null without one */
  title?: { href: string; tip: string } | null };
export type SiteBannerActions = { byMail: (messageId: string) => void; tryAgain: (messageId: string) => void };

/** the ticket's site as its mark names it: "host/folder" for a site in a subfolder (2026-10-11), else the host, else the name; null without a site */
export const siteHostOf = (ticket: Ticket): string | null => (ticket.site ? ((ticket.site.url ? siteLabel(ticket.site.url) : null) ?? ticket.site.host ?? ticket.site.name) : null);
const isWebAddress = (u: string): boolean => { try { return ["http:", "https:"].includes(new URL(u).protocol); } catch { return false; } };

export function siteTicketView(ticket: Ticket, actions?: SiteBannerActions): SiteTicketView {
  const host = siteHostOf(ticket);
  if (!host) return { banner: null, title: null };
  const url = ticket.external?.url ?? null;
  const mark = <SiteMark host={host} href={url ?? undefined} isPublic={!!ticket.public} iconOnly />; // one pill in place of the channel label (2026-10-11); the icon alone with a tooltip since 2026-10-27
  const title = url && isWebAddress(url) ? { href: url, tip: `Open on ${host}` } : null; // the API refuses other schemes; the screen checks again
  return { mark, banner: actions ? notDelivered(ticket, host, actions) : null, title };
}

/** the banner's two buttons through the API; `run` says it in a toast and reads the ticket again */
export const siteBannerActions = (slug: string, ticket: Ticket, run: (write: Promise<unknown>, done: string) => void): SiteBannerActions => ({
  byMail: (id) => run(siteReplies.byMail(slug, ticket.number, id), "Sent by mail"),
  tryAgain: (id) => run(siteReplies.tryAgain(slug, ticket.number, id), `Sent to ${ticket.site?.host ?? "the site"} again`),
});

/** the line under a reply on its way to the ticket's site, or undefined for a reply that never went to one */
export const siteLineOf = (m: Ticket["messages"][number]): string | undefined => (m.delivery?.site ? siteDeliveryLine(m.delivery.site) : undefined);

/** the newest reply the site never took that waits for the team: "Send by mail" (the mail rules apply from there) or "Try again" */
function notDelivered(ticket: Ticket, host: string, actions: SiteBannerActions): ReactNode {
  const failed = [...ticket.messages].reverse().find((m) => m.delivery?.site?.state === "failed");
  if (!failed) return null;
  const first = ticket.customer.name.split(" ")[0] || ticket.customer.name;
  return (
    <div className="ob-site-banner">
      <Notice icon="cloudOff" action={<span className="ob-site-banner__actions"><Button size="sm" variant="primary" onClick={() => actions.byMail(failed.id)}>Send by mail</Button><Button size="sm" onClick={() => actions.tryAgain(failed.id)}>Try again</Button></span>}>
        <strong>Not delivered to {host}.</strong> The site did not take the reply, and {first}&apos;s address is not confirmed, so nothing was mailed.
      </Notice>
    </div>
  );
}

/** M12 round four: the card's addresses beside the ticket (primary, the addresses sites added, "Unconfirmed", the ids sites know the person
 *  by), shown only when there is more than the one address */
export function SiteAddresses({ addresses }: { addresses?: Customer["addresses"] }): ReactNode {
  const rows = addressRows(addresses ?? []);
  return rows ? <CustomerAddresses rows={rows} /> : null;
}
